import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { notify } from '@/actions/ui';
import AddUserDialog from '@/components/AddUserDialog';
import Link from '@/components/Link';
import ERROR_CODES from '@/core/apiErrorCodes';
import { darkGray } from '@/theme/colors';

function AddTeamMemberDialog({
  groupId,
  addTeamMember,
  inviteUser,
  notify,
  onRequestDone,
  isSMB,
  seatsAvailable,
  settingsUrl,
  ...rest
}: any): JSX.Element {
  const [selectedRole, setSelectedRole] = useState<'member' | 'viewer'>(
    seatsAvailable <= 0 ? 'viewer' : 'member'
  );

  useEffect(() => {
    if (seatsAvailable <= 0) {
      setSelectedRole('viewer');
    }
  }, [seatsAvailable]);

  const done = (ref: any) => {
    if (onRequestDone) {
      onRequestDone(ref);
    }
  };

  const role = isSMB ? selectedRole : 'member';

  const handleRequestAdd = (values: any) => {
    const usersAdded = values
      .filter((v: any) => v.type === 'user')
      .map((u: any) => ({
        userId: u.value.id,
        name: u.value.name,
        role,
      }))
      .map((member: any) => ({
        ref: member.name,
        promise: addTeamMember(groupId, member),
      }));

    const usersInvited = values
      .filter((v: any) => v.type === 'email')
      .map((v: any) => ({
        email: v.value.email,
        collectionType: 'group',
        collectionId: groupId,
        role,
      }))
      .map((member: any) => ({
        ref: member.email,
        promise: inviteUser(member),
      }));

    [...usersAdded, ...usersInvited].forEach(({ ref, promise }) => {
      promise.then(
        () => {
          done(ref);

          notify(`${ref} added successfully.`, 'success', 3000);
        },
        (e: any) => {
          done(ref);

          if (e.isPermissionError) {
            notify(`You don't have permission to add ${ref}}.`, 'error', 3000);
          } else if (
            e.graphQLErrors?.[0]?.extensions?.code === ERROR_CODES.BILLING_SEATS_EXHAUSTED
          ) {
            notify('No seats available. Add seats in Settings & Billing.', 'error', 3000);
          } else if (e.message === 'GraphQL Error: member already exists') {
            notify(`${ref} was already a member`, 'warning', 3000);
          } else if (e.message && e.message.startsWith('GraphQL Error: user with email')) {
            notify(`${ref} already a user, no invitation sent`, 'error', 3000);
          } else {
            notify(`An error occurred when adding ${ref}.`, 'error', 3000);
          }
        }
      );
    });
  };

  const explanationStyle = { fontSize: 12, color: darkGray };

  const rolePicker = isSMB ? (
    <div style={{ marginBottom: 8 }}>
      <RadioGroup
        value={selectedRole}
        onChange={(e) => setSelectedRole(e.target.value as 'member' | 'viewer')}
      >
        <FormControlLabel
          value="member"
          control={<Radio size="small" />}
          label={
            <span>
              Member{seatsAvailable <= 0 && <span> (no seats available)</span>}
              {seatsAvailable <= 0 && (
                <span style={{ ...explanationStyle, marginLeft: 8 }}>
                  Add seats in{' '}
                  <Link
                    to={`${settingsUrl}/settings`}
                    style={explanationStyle}
                    className="hover:underline"
                  >
                    Settings & Billing
                  </Link>
                </span>
              )}
            </span>
          }
          disabled={seatsAvailable <= 0}
        />
        <FormControlLabel
          value="viewer"
          control={<Radio size="small" />}
          label={
            <span>
              Viewer
              <span style={{ ...explanationStyle, marginLeft: 8 }}>
                Doesn&apos;t require a seat
              </span>
            </span>
          }
        />
      </RadioGroup>
    </div>
  ) : undefined;

  return (
    <AddUserDialog
      title="Add team member"
      subTitle={rolePicker}
      allowEmails
      onRequestAdd={handleRequestAdd}
      {...rest}
    />
  );
}

// @ts-expect-error TS(2630) FIXME: Cannot assign to 'AddTeamMemberDialog' because it ... Remove this comment to see the full error message
AddTeamMemberDialog = connect(undefined, {
  notify,
})(AddTeamMemberDialog);

export default AddTeamMemberDialog;
